import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { endOfDay, startOfDay } from 'date-fns';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { UserNonConformancesRoutes } from 'src/app/core/enums/routes.enum';
import { DashboardDepartmentChangeService } from 'src/app/core/services/dashboard-department-change.service';
import { ClearFilterDetails } from 'src/app/core/state/main-table-filter.actions';
import { NonConformanceStatus } from 'src/app/non-conformance-user/enums/non-conformance-status.enum';
import { INonConformanceReportsStatusResponse } from 'src/app/non-conformance-user/models/non-conformance-reports-status-response.model';
import {
  LoadOpenReportsForModal,
  LoadReportsStatus,
} from 'src/app/non-conformance-user/state/non-conformance.actions';
import { NonConformanceState } from 'src/app/non-conformance-user/state/non-conformance.state';
import { AlertStatus } from 'src/app/risk-assessment-user/enums/alert-status.enum';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { DashboardListModalComponent } from 'src/app/shared/dashboard/deviations-over-time/dashboard-list-modal/dashboard-list-modal.component';
import { TimePeriod } from 'src/app/shared/dashboard/models/time-period.enum';
import { MainTableFilterOperand } from 'src/app/shared/main-table-module/main-table/main-table-column-filter/main-table-column-filter.component';
import { IdName } from 'src/app/shared/models/id-name';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from 'src/app/shared/models/main-table-filter.model';
import { WidgetBlock } from 'src/app/shared/ui-elements/ui-widget-card/ui-widget-card.component';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';

const MODAL_PAGE_SIZE = 100;

@Component({
  selector: 'app-reports-dashboard-widgets',
  templateUrl: './reports-dashboard-widgets.component.html',
  styleUrls: ['./reports-dashboard-widgets.component.scss'],
  standalone: false,
})
export class ReportsDashboardWidgetsComponent implements OnInit, OnDestroy {
  createdReports = 0;
  openReports = 0;
  completedReports = 0;
  closedReports = 0;
  reportRoutes = UserNonConformancesRoutes;

  expiredReportsData: WidgetBlock[] = [];
  dueSoonReportsData: WidgetBlock[] = [];

  loadingActions = [LoadReportsStatus];

  // Loading states for individual widgets
  openReportsLoading = false;
  dueSoonReportsLoading = false;
  expiredReportsLoading = false;

  vesselControl = new FormControl<string>('', { nonNullable: true });
  vesselOptions = select(TenantUserAccessState.getAllAvailableDepartments);

  periodControl = new FormControl(TimePeriod.Last30Days);
  periodOptions: IdName[] = [
    { id: TimePeriod.AllTime, name: 'allTime' },
    { id: TimePeriod.Last7Days, name: 'last7Days' },
    { id: TimePeriod.Last30Days, name: 'last30Days' },
    { id: TimePeriod.Last90Days, name: 'last90Days' },
    { id: TimePeriod.YearToDate, name: 'yearToDate' },
  ];

  customRangeControl = new FormControl<{ from: Date; to: Date } | null>(null);
  maxDate = new Date();

  private subscription = new Subscription();
  private suppressCustomRangeChange = false;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private dashboardDepartmentChangeService: DashboardDepartmentChangeService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {}

  ngOnInit(): void {
    this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
    this.loadReportsCounts();

    // Reload when vessel filter changes
    this.subscription.add(
      this.vesselControl.valueChanges.subscribe(() => {
        this.loadReportsCounts();
      })
    );

    // Reload when period filter changes
    this.subscription.add(
      this.periodControl.valueChanges.subscribe((period) => {
        // Clear custom range when a predefined period is selected
        if (period !== TimePeriod.Custom) {
          this.clearCustomRangeFilters();
        }
        this.loadReportsCounts();
      })
    );

    // Handle custom date range changes
    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.handleCustomRangeChange(range);
      })
    );

    // Reload data when tenant changes
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.currentTenant)
        .pipe(
          filter((tenant) => !!tenant),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
          skip(1) // Skip the initial emission to avoid duplicate loads on component init
        )
        .subscribe(() => {
          // Reset filters when tenant changes
          this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
          this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
          this.clearCustomRangeFilters();
          // Note: Data reload happens in department change handler (tenant change triggers department change)
        })
    );

    // Reload data when department changes
    this.subscription.add(
      this.dashboardDepartmentChangeService.getDepartmentChanges().subscribe((departmentId) => {
        this.vesselControl.setValue(departmentId, { emitEvent: false });
        this.loadReportsCounts();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.store.dispatch(new ClearFilterDetails());
  }

  private getDateRangeFromPeriod(period: string): { from?: string; to?: string } {
    if (!period || period === TimePeriod.AllTime) {
      return {};
    }

    if (period === TimePeriod.Custom) {
      const customRange = this.customRangeControl.value;
      return customRange?.from
        ? {
            from: startOfDay(new Date(customRange.from)).toISOString(),
            to: customRange.to ? endOfDay(new Date(customRange.to)).toISOString() : undefined,
          }
        : {};
    }

    if (period === TimePeriod.YearToDate) {
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      return { from: yearStart.toISOString() };
    }

    const now = new Date();
    const startDate = new Date(now);

    switch (period) {
      case TimePeriod.Last7Days:
        startDate.setDate(now.getDate() - 7);
        break;
      case TimePeriod.Last30Days:
        startDate.setDate(now.getDate() - 30);
        break;
      case TimePeriod.Last90Days:
        startDate.setDate(now.getDate() - 90);
        break;
      default:
        return {};
    }

    startDate.setHours(0, 0, 0, 0);
    return { from: startDate.toISOString() };
  }

  private handleCustomRangeChange(range: { from: Date; to: Date } | null): void {
    if (this.suppressCustomRangeChange) {
      return;
    }

    if (range) {
      this.periodControl.setValue(TimePeriod.Custom, { emitEvent: false });
      this.loadReportsCounts();
      return;
    }

    if (this.periodControl.value === TimePeriod.Custom) {
      this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
      this.loadReportsCounts();
    }
  }

  private clearCustomRangeFilters(): void {
    this.suppressCustomRangeChange = true;
    this.customRangeControl.setValue(null);
    this.suppressCustomRangeChange = false;
  }

  private loadReportsCounts(): void {
    if (!this.tenantUserPrivileges.userHasPrivilege(Privileges.NonConformanceRead)) {
      return;
    }

    const departmentId = this.vesselControl.value;
    const dateRange = this.getDateRangeFromPeriod(
      this.periodControl.value || TimePeriod.Last30Days
    );

    this.store.dispatch(new LoadReportsStatus([departmentId], dateRange.from, dateRange.to));

    this.subscription.add(
      this.store
        .select(NonConformanceState.getReportsStatus)
        .subscribe((status: INonConformanceReportsStatusResponse | undefined) => {
          if (!status) {
            return;
          }

          this.createdReports = status.totalReports;
          this.openReports = status.allReportsInProgress;
          this.completedReports = status.completedReports;
          this.closedReports = status.closedReports;

          // Expired reports data: [my reports, all reports]
          this.expiredReportsData = [
            {
              name: 'myExpiredReports',
              value: status.myReportsExpired,
              tooltip: 'myExpiredReportsTooltip',
            },
            {
              name: 'allExpiredReports',
              value: status.allReportsExpired,
              icon: 'error_outline',
              iconColor: 'red',
              tooltip: 'allExpiredReportsTooltip',
            },
          ];

          // Due soon reports data: [my reports, all reports]
          this.dueSoonReportsData = [
            {
              name: 'myDueSoonReports',
              value: status.myReportsExpiring,
              tooltip: 'myDueSoonReportsTooltip',
            },
            {
              name: 'allDueSoonReports',
              value: status.allReportsExpiring,
              icon: 'warning_amber',
              iconColor: 'yellow',
              tooltip: 'allDueSoonReportsTooltip',
            },
          ];
        })
    );
  }

  openExpiredReportsInOpen(): void {
    this.expiredReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Open,
      AlertStatus.Expired,
      'expiredReportsInOpenState'
    );
  }

  openExpiredReportsInCompleted(): void {
    this.expiredReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Completed,
      AlertStatus.Expired,
      'expiredReportsInCompletedState'
    );
  }

  openDueSoonReportsInOpen(): void {
    this.dueSoonReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Open,
      AlertStatus.Expiring,
      'dueSoonReportsInOpenState'
    );
  }

  openDueSoonReportsInCompleted(): void {
    this.dueSoonReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Completed,
      AlertStatus.Expiring,
      'dueSoonReportsInCompletedState'
    );
  }

  navigateToOpenReports(): void {
    this.openReportsLoading = true;
    this.openReportsModal(NonConformanceStatus.Open, undefined, 'openReportsTitle');
  }

  private openReportsModal(
    status: NonConformanceStatus,
    alertStatus: AlertStatus | undefined,
    titleKey: string
  ): void {
    const filter = this.createFilter(MODAL_PAGE_SIZE, status, alertStatus);

    this.store.dispatch(new LoadOpenReportsForModal(filter, false)).subscribe(() => {
      this.openReportsLoading = false;
      this.dueSoonReportsLoading = false;
      this.expiredReportsLoading = false;

      this.dialog.open(DashboardListModalComponent, {
        data: {
          period: '',
          items: this.store.selectSnapshot(NonConformanceState.getOpenReportsModalData),
          titleKey,
          dateField: 'nonConformanceDate',
          sortByDate: false,
        },
      });
    });
  }

  private createFilter(
    pageSize: number,
    status: NonConformanceStatus,
    alertStatus?: AlertStatus
  ): MainTableFilterModel {
    const filter = new MainTableFilterModel(MainTableFilterModulesEnum.NonConformances);
    filter.pageSize = pageSize;
    filter.pageIndex = 0;
    filter.additional = status;
    filter.orderBy = 'AlertStatus';
    filter.ascDesc = 'Descending';

    const searchByFilters: MainTableFilterSearchByModel[] = [];

    // Add alert status filter if provided
    if (alertStatus !== undefined) {
      searchByFilters.push(
        new MainTableFilterSearchByModel(
          'AlertStatus',
          [alertStatus.toString()],
          MainTableFilterOperand.In
        )
      );
    }

    // Add department filter if specific departments are selected
    const selectedVesselIds = this.vesselControl.value;
    if (selectedVesselIds && selectedVesselIds.length > 0) {
      const vessels =
        this.store.selectSnapshot(TenantUserAccessState.getAllAvailableDepartments) || [];
      const selectedVesselNames = vessels
        .filter((v: IdName) => selectedVesselIds.includes(v.id))
        .map((v: IdName) => v.name);

      if (selectedVesselNames.length > 0) {
        searchByFilters.push(
          new MainTableFilterSearchByModel(
            'Department.Name',
            selectedVesselNames,
            MainTableFilterOperand.In
          )
        );
      }
    }

    if (searchByFilters.length > 0) {
      filter.searchBy = searchByFilters;
    }

    return filter;
  }
}
